import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, Loader2 } from 'lucide-react'
import { axiosInstance } from '@/api/axios-instance'
import { Button } from '@/components/shadcn/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/shadcn/tooltip'
import { notify } from '@/lib/notify'
import type { TravelExpenseEntriesFilters } from '../hooks/useTravelExpenseEntries'
import { toLocalDate } from '../hooks/useTravelExpenseEntries'

interface Props {
  moduleId: number
  /** Same filters as the table: the export covers exactly the filtered entries (all pages). */
  filters: TravelExpenseEntriesFilters
}

/**
 * Downloads the filtered entries as Excel (sums on top, one row per entry). Download goes via
 * `axiosInstance` with `responseType: 'blob'` (allowed exception, see conventions).
 */
export function TravelExpenseExportButton({ moduleId, filters }: Props) {
  const { t } = useTranslation('service-modules-travel-expenses')
  const [isPending, setIsPending] = useState(false)

  async function handleExport() {
    setIsPending(true)
    try {
      const res = await axiosInstance.get(
        `/api/companies/current/servicemodules/travelexpenses/${moduleId}/entries/company/export`,
        {
          params: {
            employeeUserId: filters.employeeUserId,
            startDate: toLocalDate(filters.from),
            endDate: toLocalDate(filters.to),
          },
          responseType: 'blob',
        },
      )

      const url = window.URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'Reisekosten.xlsx'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      notify.apiError(error, t('export.error'))
    } finally {
      setIsPending(false)
    }
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            disabled={isPending}
            aria-label={t('export.button')}
            onClick={handleExport}
          >
            {isPending ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('export.button')}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
